import { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { AttachFileOutlined } from "@mui/icons-material";
import { V2_COLORS } from "@/pages/InvoiceManagement/v2/constants/colors";
import type { RfqAttachment, VendorRfqLine } from "../../types";

interface LineDescriptionProps {
  line: VendorRfqLine;
  full?: boolean;
}

const attachmentsLabel = (count: number): string => (count === 0 ? "No attachments" : `${count} attachment${count === 1 ? "" : "s"}`);

const clampSx = { display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" };

const chipSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  px: 1,
  py: 0.5,
  borderRadius: "16px",
  backgroundColor: V2_COLORS.primaryBg,
  color: V2_COLORS.primary,
  fontSize: "11px",
  textDecoration: "none",
};

function AttachmentChip({ attachment }: { attachment: RfqAttachment }) {
  const content = (
    <>
      <AttachFileOutlined sx={{ fontSize: 13 }} />
      {attachment.name}
    </>
  );
  if (!attachment.url) {
    return (
      <Box component="span" sx={chipSx}>
        {content}
      </Box>
    );
  }
  return (
    <Box component="a" href={attachment.url} target="_blank" rel="noreferrer" sx={chipSx}>
      {content}
    </Box>
  );
}

export default function LineDescription({ line, full = false }: LineDescriptionProps) {
  const [expanded, setExpanded] = useState(false);
  const showAll = full || expanded;
  const text = [line.specification, line.description].filter(Boolean).join(" · ");

  return (
    <Box>
      <Typography fontSize="12px" sx={showAll ? undefined : clampSx}>
        {text}
      </Typography>
      {!full && (
        <Button
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          sx={{ textTransform: "none", p: 0, minWidth: 0, fontSize: "12px", mt: 0.5 }}
        >
          {expanded ? "Show less" : "Show full description"}
        </Button>
      )}
      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
        {line.attachments.map((attachment) => (
          <AttachmentChip key={attachment.id} attachment={attachment} />
        ))}
      </Stack>
      <Typography fontSize="11px" sx={{ color: V2_COLORS.textSecondary, mt: 0.5 }}>
        {attachmentsLabel(line.attachments.length)}
      </Typography>
    </Box>
  );
}
